import { BaseLayout } from '@/components/layout/BaseLayout'
import { PageHeader } from '@/components/common/PageHeader'
import { EmptyState } from '@/components/common/EmptyState'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { Card, CardContent, CardFooter, CardTitle } from '@/components/common/Card'
import { useStore } from '@/features/stores/StoreContext'
import { supabase } from '@/lib/supabase/client'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Plus, Pencil, Trash2, FolderTree } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

interface Category {
  id: string
  name: string
  slug: string
  description: string
  parent_id: string | null
  created_at: string
}

export function CategoriesPage() {
  const { currentStore } = useStore()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null)
  const [editingCategory, setEditingCategory] = useState<Category | null>(null)
  const [formData, setFormData] = useState({ name: '', description: '', parent_id: '' })

  const { data: categories = [], isLoading } = useQuery({
    queryKey: ['categories', currentStore?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .eq('store_id', currentStore?.id)
        .order('created_at', { ascending: false })
      if (error) throw error
      return data as Category[]
    },
    enabled: !!currentStore?.id,
  })

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const slug = data.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')
      const { error } = await supabase.from('categories').insert({
        store_id: currentStore?.id,
        name: data.name,
        slug,
        description: data.description,
        parent_id: data.parent_id || null,
      })
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] })
      toast.success('Category created successfully')
      setOpen(false)
      setFormData({ name: '', description: '', parent_id: '' })
    },
    onError: () => toast.error('Failed to create category'),
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: typeof formData }) => {
      const slug = data.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')
      const { error } = await supabase
        .from('categories')
        .update({
          name: data.name,
          slug,
          description: data.description,
          parent_id: data.parent_id || null,
        })
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] })
      toast.success('Category updated successfully')
      setOpen(false)
      setEditingCategory(null)
      setFormData({ name: '', description: '', parent_id: '' })
    },
    onError: () => toast.error('Failed to update category'),
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('categories').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] })
      toast.success('Category deleted successfully')
    },
    onError: () => toast.error('Failed to delete category'),
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (editingCategory) {
      updateMutation.mutate({ id: editingCategory.id, data: formData })
    } else {
      createMutation.mutate(formData)
    }
  }

  const handleEdit = (category: Category, e?: React.MouseEvent) => {
    e?.stopPropagation()
    setEditingCategory(category)
    setFormData({
      name: category.name,
      description: category.description || '',
      parent_id: category.parent_id || '',
    })
    setOpen(true)
  }

  const handleCardClick = (category: Category) => {
    setSelectedCategory(category)
    setDetailsOpen(true)
  }

  const handleDelete = (id: string, e: React.MouseEvent) => {
    e.stopPropagation()
    deleteMutation.mutate(id)
  }

  const handleAddSubcategory = () => {
    setFormData({ name: '', description: '', parent_id: selectedCategory?.id || '' })
    setDetailsOpen(false)
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    setEditingCategory(null)
    setFormData({ name: '', description: '', parent_id: '' })
  }

  const handleDetailsClose = () => {
    setDetailsOpen(false)
    setSelectedCategory(null)
  }

  const parentCategories = categories.filter((c) => !c.parent_id)

  return (
    <BaseLayout>
      <div className="p-8">
        <PageHeader
          title="Categories"
          description="Organize your products with categories"
          action={
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => handleClose()}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Category
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingCategory ? 'Edit Category' : 'Create Category'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label>Name</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Enter category name"
                      required
                    />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Enter category description"
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label>Parent Category (Optional)</Label>
                    <select
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      value={formData.parent_id}
                      onChange={(e) => setFormData({ ...formData, parent_id: e.target.value })}
                    >
                      <option value="">None (Top Level)</option>
                      {parentCategories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex gap-2 pt-4">
                    <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                      {editingCategory ? 'Update Category' : 'Create Category'}
                    </Button>
                    <Button type="button" variant="outline" onClick={handleClose}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          }
        />

        {isLoading ? (
          <LoadingSpinner />
        ) : categories.length === 0 ? (
          <EmptyState
            icon={FolderTree}
            title="No categories yet"
            description="Create categories to organize your products and improve navigation"
            action={{ label: 'Add Category', onClick: () => setOpen(true) }}
          />
        ) : (
          <div className="space-y-6">
            {/* Parent Categories */}
            <div>
              <h2 className="text-lg font-semibold mb-4">Parent Categories</h2>
              <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-4">
                {parentCategories.map((category) => {
                  const subcategories = categories.filter((c) => c.parent_id === category.id)
                  return (
                    <div key={category.id} onClick={() => handleCardClick(category)}>
                      <Card hover className="group cursor-pointer">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2 flex-1">
                            <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                              <FolderTree className="h-4 w-4 text-primary" />
                            </div>
                            <div>
                              <CardTitle className="text-sm">{category.name}</CardTitle>
                              <p className="text-xs text-muted-foreground">
                                {subcategories.length} sub
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                            <Button size="sm" variant="ghost" onClick={(e) => handleEdit(category, e)}>
                              <Pencil className="h-3 w-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => handleDelete(category.id, e)}
                              disabled={deleteMutation.isPending}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                        {category.description && (
                          <p className="text-xs text-muted-foreground line-clamp-1 mt-1">
                            {category.description}
                          </p>
                        )}
                      </Card>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Subcategories */}
            {parentCategories.map((parent) => {
              const subcategories = categories.filter((c) => c.parent_id === parent.id)
              if (subcategories.length === 0) return null
              return (
                <div key={parent.id}>
                  <h2 className="text-lg font-semibold mb-4">{parent.name} Subcategories</h2>
                  <div className="grid gap-3 md:grid-cols-4 lg:grid-cols-5">
                    {subcategories.map((category) => (
                      <div key={category.id} onClick={() => handleCardClick(category)}>
                        <Card hover className="group cursor-pointer">
                          <div className="flex items-center justify-between mb-2">
                            <CardTitle className="text-sm flex-1">{category.name}</CardTitle>
                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                              <Button size="sm" variant="ghost" onClick={(e) => handleEdit(category, e)}>
                                <Pencil className="h-3 w-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => handleDelete(category.id, e)}
                                disabled={deleteMutation.isPending}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                          {category.description && (
                            <p className="text-xs text-muted-foreground line-clamp-1">
                              {category.description}
                            </p>
                          )}
                        </Card>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Category Details Dialog */}
        {selectedCategory && (
          <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{selectedCategory.name}</DialogTitle>
            </DialogHeader>
            {selectedCategory && (
              <div className="space-y-4">
                <div>
                  <Label className="text-muted-foreground">Description</Label>
                  <p className="text-sm mt-1">{selectedCategory.description || 'No description'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Type</Label>
                  <p className="text-sm mt-1">{selectedCategory.parent_id ? 'Subcategory' : 'Parent Category'}</p>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>Subcategories ({categories.filter(c => c.parent_id === selectedCategory.id).length})</Label>
                    {!selectedCategory.parent_id && (
                      <Button size="sm" onClick={handleAddSubcategory}>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Subcategory
                      </Button>
                    )}
                  </div>
                  {!selectedCategory.parent_id && (
                    <div className="grid gap-2 max-h-60 overflow-y-auto">
                      {categories.filter(c => c.parent_id === selectedCategory.id).map((sub) => (
                        <div key={sub.id} className="flex items-center justify-between p-3 border rounded-lg">
                          <div>
                            <p className="font-medium text-sm">{sub.name}</p>
                            <p className="text-xs text-muted-foreground">{sub.description}</p>
                          </div>
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={(e) => { handleEdit(sub, e); handleDetailsClose(); }}>
                              <Pencil className="h-3 w-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => handleDelete(sub.id, e)}
                              disabled={deleteMutation.isPending}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex gap-2 pt-2 border-t">
                  <Button onClick={() => { handleEdit(selectedCategory); handleDetailsClose(); }}>
                    <Pencil className="h-4 w-4 mr-2" />
                    Edit Category
                  </Button>
                  <Button variant="outline" onClick={handleDetailsClose}>
                    Close
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
        )}
      </div>
    </BaseLayout>
  )
}
